/*---------------------------------- Followed the example on this link ----------------------------------

	https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Client-side_web_APIs/Video_and_audio_APIs

----------------------------------------------------------------------------------------------------------*/

import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation, OnChanges, Renderer2 } from '@angular/core';
import { Observe } from '../custom-decorators/Observe';
import { BehaviorSubject, Observable, Subject, Subscription, from, fromEvent } from 'rxjs';
import { last, debounceTime, map } from 'rxjs/operators'
import { debounce } from 'lodash';

export type VideoPlayerOptions = {
	title : string;
	fluid? : boolean;
	aspectRatio? : string;
	autoplay : boolean;
	sources : {
		source : string;
		type : string;
	}[];
	controls? : boolean;
	preload : "auto" | "none" | "metadata";
	breakpoints? : {
		tiny? : number;
		xsmall? : number;
		small? : number;
		medium? : number;
		large? : number;
		xlarge? : number;
		huge? : number;
	}
};


@Component({
	selector: 'app-angular-video-player',
	templateUrl: './angular-video-player.component.html',
	styleUrls: ['./angular-video-player.component.scss']
})
export class AngularVideoPlayerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

	@Input() videoSources : { source : string; type : string }[] = [];
	
	@Input() options : VideoPlayerOptions;
	
	@Observe("options") options$! : Observable<VideoPlayerOptions>;
	
	@ViewChild("videoPlayer") playerRef : any;
	@ViewChild("controls", { read : ElementRef } ) controlsRef : ElementRef;
	@ViewChild("playButton", { read : ElementRef }) playButtonRef : ElementRef;
	@ViewChild("stopButton", { read : ElementRef } ) stopButtonRef : ElementRef;
	@ViewChild("timer", { read : ElementRef } ) timerWrapperRef : ElementRef;
	@ViewChild("rewindButton", { read : ElementRef } ) rewindButtonRef : ElementRef;
	@ViewChild("fastForwardButton", { read : ElementRef } ) fastForwardButtonRef : ElementRef;
	@ViewChild("speakerButton", { read : ElementRef } ) speakerButtonRef : ElementRef;
	@ViewChild("volumeControl", { read : ElementRef } ) volumeControlRef : ElementRef;

	private initialVolume = 0.5;	
	public volumeLevel$ : BehaviorSubject<number> = new BehaviorSubject<number>(this.initialVolume);
	
	private volumeLevelSub : Subscription;
	private muted$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private mutedSub : Subscription ;

	public paused : boolean = true;
	

	public player : HTMLVideoElement; 	
	private fwdButton : HTMLButtonElement;
	private rwdButton : HTMLButtonElement;
	private playButton : HTMLButtonElement;
	private speakerButton : HTMLButtonElement;
	private volumeControl : HTMLInputElement;
	private timerWrapper : HTMLDivElement;
	private timer : HTMLSpanElement;
	private timerBar : HTMLDivElement;


	private intervalFwd : NodeJS.Timer;
	private intervalRwd : NodeJS.Timer;
	private intervalSpeakerHover : NodeJS.Timer;
	public volumeControlShown : boolean = false;

	constructor(private elementRef: ElementRef,
		private renderer : Renderer2)
	{

	}

	ngOnInit(): void {
		this.mutedSub = this.muted$.subscribe((value : boolean) => this.mutePlayer(value));			
	}

	ngOnDestroy(): void {
		this.mutedSub && this.mutedSub?.unsubscribe();
		this.volumeLevelSub && this.volumeLevelSub?.unsubscribe();
	}

	ngAfterViewInit(): void {
		if(!this.playerRef?.nativeElement)
		{
			return;
		}

		this.player = this.playerRef.nativeElement;
		this.player && this.renderer.removeAttribute(this.player, "controls");			
		
		if(this.controlsRef?.nativeElement)
		{
			this.renderer.setStyle(this.controlsRef?.nativeElement, "visibility", "visible");
		}

		this.playButton = this.playButtonRef?.nativeElement;
		this.fwdButton = this.fastForwardButtonRef?.nativeElement;
		this.rwdButton = this.rewindButtonRef?.nativeElement;
		this.timerWrapper = this.timerWrapperRef?.nativeElement;
		this.speakerButton = this.speakerButtonRef?.nativeElement;
		this.volumeControl = this.volumeControlRef?.nativeElement;

		if(this.timerWrapper)
		{
			this.timer = this.timerWrapper.querySelector("span") as HTMLSpanElement;
			this.timerBar = this.timerWrapper.querySelector("div") as HTMLDivElement;
		}

		this.onTimeUpdated(undefined);
	}

	ngOnChanges() : void {
		
	}

	onPlayClicked = (event : any) => {
		if(!this.player || !this.rwdButton || !this.fwdButton)
		{
			return;
		}

		this.renderer.removeClass(this.rwdButton, "active");
		this.renderer.removeClass(this.fwdButton, "active");		
		clearInterval(this.intervalRwd);
		clearInterval(this.intervalFwd);

		if(this.player.paused)
		{
			this.renderer.setAttribute(this.player, "data-icon", "u");
			this.player.play();
		}
		else
		{
			this.renderer.setAttribute(this.player, "data-icon", "P");
			this.player.pause();
		}
	}	

	public stopMedia = () => {
		if(!this.player || !this.rwdButton || !this.fwdButton)
		{
			return;
		}

		this.player.pause();
		this.player.currentTime = 0;
		this.renderer.setAttribute(this.player, "data-icon", "P");

		this.renderer.removeClass(this.rwdButton, "active");
		this.renderer.removeClass(this.fwdButton, "active");		
		clearInterval(this.intervalRwd);
		clearInterval(this.intervalFwd);
	}	

	public onRewindClicked = (event : any) => {
		if(!this.fwdButton || !this.rwdButton || !this.player)
		{
			return;
		}
		
		clearInterval(this.intervalFwd);

		this.renderer.removeClass(this.fwdButton, "active");

		if (this.rwdButton.classList.contains("active")) 
		{
			this.renderer.removeClass(this.rwdButton, "active");
			clearInterval(this.intervalRwd);
			this.player.play();
		} 
		else 
		{
			this.renderer.addClass(this.rwdButton, "active");	
			this.player.pause();
			this.intervalRwd = setInterval(this.windBackward, 200);
		}
	}

	public onFastForwardClicked = (event : any) => 
	{
		if(!this.fwdButton || !this.rwdButton || !this.player)
		{
			return;
		}
	
		clearInterval(this.intervalRwd);

		this.renderer.removeClass(this.rwdButton, "active");

		if (this.fwdButton.classList.contains("active")) 
		{
			this.renderer.removeClass(this.fwdButton, "active");			
			clearInterval(this.intervalFwd);
			this.player.play();
		} 
		else 
		{
			this.renderer.addClass(this.fwdButton, "active");			
			this.player.pause();
			this.intervalFwd = setInterval(this.windForward, 200);
		}
	}

	private windBackward = () => 
	{ 
		if(!this.fwdButton || !this.rwdButton || !this.player)
		{
			return;
		}
		
		if (this.player.currentTime <= 3) 
		{
			this.renderer.removeClass(this.rwdButton, "active");
			clearInterval(this.intervalRwd);
			this.stopMedia();
		} 
		else 
		{
			this.player.currentTime -= 3;
		}
	}

	private windForward = () => 
	{ 
		if(!this.fwdButton || !this.rwdButton || !this.player)
		{
			return;
		}
		
		if (this.player.currentTime >= this.player.duration - 3) 
		{
			this.renderer.removeClass(this.fwdButton, "active");			
			clearInterval(this.intervalFwd);
			this.stopMedia();
		} 
		else 
		{
			this.player.currentTime += 3;
		}
	}

	public onTimeUpdated = (event : any) => {
		if(!this.player || !this.timerWrapper || !this.timer || !this.timerBar)
		{
			return;
		}		
	  
		const currentTime = this.getFormattedMediaTime(this.player.currentTime); 
		const totalTime = this.getFormattedMediaTime(this.player.duration);	

		const mediaTime = currentTime && totalTime ? `${currentTime}/${totalTime}` : 
			currentTime ? currentTime : "";
		this.timer.textContent = mediaTime;
		const barLength = this.timerWrapper.clientWidth * (this.player.currentTime / this.player.duration);
		this.renderer.setStyle(this.timerBar, "width", `${barLength}px`);				
	}
	

	getFormattedMediaTime = (timeDuration : number) => {
		let timeStr = "";
		if(Number.isNaN(timeDuration))
		{
			return timeStr;
		}
		const hours = Math.floor(timeDuration / 3600);
		const minutes = Math.floor((timeDuration - hours * 3600) / 60);
		const seconds = Math.floor((timeDuration - hours * 3600) - minutes * 60);

		const hoursValue = Number.isNaN(hours) ? "" : hours.toString().padStart(2, "0");
		const minutesValue = Number.isNaN(minutes) ? "" : minutes.toString().padStart(2, "0");
		const secondsValue = Number.isNaN(seconds) ? "" : seconds.toString().padStart(2, "0");

		if(hoursValue + minutesValue + secondsValue)
		{
			timeStr = hours > 1 ? `${hoursValue}:${minutesValue}:${secondsValue}` :  `${minutesValue}:${secondsValue}`;
		}
		return timeStr;
	}

	onSpeakerClicked = (event : any) => {		
		this.muted$.next(!this.player?.muted);		
	}

	onVolumeChanged = (event : any) => {
		this.setMediaVolume((event?.target || event?.currentTarget).value);	
	}

	onSpeakerFocus = (event : any) => {
		this.volumeControlShown = true;
		clearInterval(this.intervalSpeakerHover);
		this.intervalSpeakerHover = setInterval(() => {
			this.volumeControlShown = false;
		}, 10 * 60 * 1000);
	}

	onLoadedDataEnd = (event : any) => {
		//console.log("Data has loaded");
	}

	private setMediaVolume = (value : number) => {
		
		if(0 >= value)
		{
			this.muted$.next(true);			
		}
		else
		{
			this.muted$.next(false);	
		}

		this.volumeLevel$.next(value);
		this.player.volume = value;
	}

	onVideoClicked = (event : any) => {
		this.paused = !this.paused;
		if(this.player)
		{
			if(this.paused)
			{
				this.stopMedia();
			}
			else
			{
				this.onPlayClicked(event);
			}			
		}
		
	}

	private mutePlayer = (value : boolean) => {
		if(!this.player || !this.speakerButton)
		{
			return;
		}
		this.player.muted = value;
		// if(!value)
		// {
		// 	this.renderer.setAttribute(this.speakerButton, "data-icon", "Q");
		// }
		// else
		// {
		// 	this.renderer.setAttribute(this.speakerButton, "data-icon", "m");
		// }
	}

	formatLabel(value: number): string 
	{
		return `${value}`;
	}

	getVolumeLevel = () => {
		return this.player?.volume ?? 0;
	}
}
